'use client';

import { useEffect, useState } from 'react';
import { useAppStore } from '@/store/app';

export const GUIDE_KEY = 'ailumix:guide:v1';
export const NEWBIE_KEY = 'ailumix:newbie';

type Lang = 'en' | 'zh-Hant' | 'zh-Hans';

const COPY: Record<Lang, { title: string; subtitle: string; steps: Array<[string, string]>; skip: string; back: string; next: string; done: string; newbieOn: string; }> = {
  en: {
    title: 'Welcome to AiLumix', subtitle: 'See volatility. Decide with data.',
    steps: [['One platform, five views', 'Market / Watchlist / Signals / Research / Me'],
      ['Real-time market intelligence', 'Liquidation alerts, funding rates, AI research and more'],
      ['Newbie mode', 'Show simpler explanations and guides across the app']],
    skip: 'Skip', back: 'Back', next: 'Next', done: 'Start now', newbieOn: 'Keep it simple',
  },
  'zh-Hans': {
    title: '欢迎使用 AiLumix', subtitle: '波动可见，决策有据',
    steps: [['一个平台，五个视角', '行情 / 自选 / 信号 / 研究 / 我的'],
      ['实时市场情报', '爆仓预警、资金费率、AI 研究、跨市场监控'],
      ['新手模式', '在全站显示更简单的解释与引导']],
    skip: '跳过', back: '上一步', next: '下一步', done: '开始使用', newbieOn: '开启新手模式',
  },
  'zh-Hant': {
    title: '歡迎使用 AiLumix', subtitle: '波動可見，決策有據',
    steps: [['一個平台，五個視角', '行情 / 自選 / 信號 / 研究 / 我的'],
      ['即時市場情報', '爆倉預警、資金費率、AI 研究、跨市場監控'],
      ['新手模式', '在整個平台顯示更簡單的解釋與引導']],
    skip: '跳過', back: '上一步', next: '下一步', done: '開始使用', newbieOn: '開啟新手模式',
  },
};

export default function FirstVisitGuide() {
  const language = useAppStore((s) => s.language) as Lang;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [newbie, setNewbie] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!localStorage.getItem(GUIDE_KEY)) {
      setOpen(true);
    }
  }, []);

  const c = COPY[language] ?? COPY['zh-Hans'];
  if (!open) return null;
  const last = step === c.steps.length - 1;

  const close = () => {
    localStorage.setItem(GUIDE_KEY, '1');
    localStorage.setItem(NEWBIE_KEY, newbie ? '1' : '0');
    setOpen(false);
  };

  const title = language === 'en' ? 'aiLumix' : 'AiLumix';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(420px, 100%)', background: '#10151c', border: '1px solid #1f2a3a', borderRadius: 16, padding: '28px 24px 20px', boxShadow: '0 12px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize: 13, color: '#4ee6a8', fontWeight: 600, letterSpacing: 0.5 }}>{title}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: '#e8eef4', marginTop: 6, lineHeight: 1.3 }}>{c.title}</div>
        <div style={{ fontSize: 13, color: '#8a97a8', marginTop: 4 }}>{c.subtitle}</div>
        <div style={{ marginTop: 24, minHeight: 84 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: '#dfe7ef' }}>{c.steps[step][0]}</div>
          <div style={{ fontSize: 13.5, color: '#94a1b4', marginTop: 8, lineHeight: 1.6 }}>{c.steps[step][1]}</div>
          {last && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={newbie} onChange={(e) => setNewbie(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#4ee6a8' }} />
              <span style={{ fontSize: 13.5, color: '#c6d0dc' }}>{c.newbieOn}</span>
            </label>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 20, justifyContent: 'center' }}>
          {c.steps.map((_, i) => (
            <div key={i} style={{ width: i === step ? 22 : 8, height: 8, borderRadius: 4, background: i === step ? '#4ee6a8' : '#2a3648', transition: 'all 0.2s' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, background: '#1a2230', color: '#aeb9c9', border: '1px solid #2a3648', cursor: 'pointer', fontSize: 14 }}>{c.back}</button>
          )}
          <button onClick={last ? close : () => setStep(step + 1)} style={{ flex: 2, padding: '10px 0', borderRadius: 10, background: '#0d9488', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            {last ? c.done : c.next}
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button onClick={close} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer' }}>{c.skip}</button>
        </div>
      </div>
    </div>
  );
}
